
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Search, MoreVertical, Plus, Upload } from "lucide-react";
import PageHeader from "../../Ui_Components/PageHeader";
import Pagination from "../../Ui_Components/Pagination";
import CustomDropdown from "../../Ui_Components/CustomDropdown";
import { Button } from "../../Ui_Components/Button";
import IngredientReasonModal, {
  ReasonModalMode,
} from "../Ingredient/IngredientReasonModal";
import BulkUploadModal from "../Ingredient/BulkUpload/BulkUploadModal";
import BulkUploadPreviewModal from "../Ingredient/BulkUpload/BulkUploadPreviewModal";
import {
  UploadDishBulkResponse,
  archiveOrRestoreDish,
  downloadDishBulkUploadErrors,
  downloadDishBulkUploadTemplate,
  getDishDashboardList,
  uploadDishBulkUpload,
} from "../../../services/dishes";
import { getAvailableNutritionistsList } from "../../../services/sales";
import { useAuth } from "../../../context/auth/AuthContext";
import { ToastContainer, useToast } from "../../Ui_Components/ToastMessage";
import { Dish } from "./types";
import { BASE_URL } from "../../api/url";
import { ReactComponent as NoImageIcon } from "../../../assects/svg/food_lib/noimage.svg";

type TabType = "all" | "active" | "archived";

const ITEMS_PER_PAGE = 10;

// Tab → backend `filter` param. "all" omits the param entirely.
const TAB_TO_FILTER: Record<TabType, "" | "active" | "archive"> = {
  all: "",
  active: "active",
  archived: "archive",
};

const toNumber = (value: unknown): number => {
  const parsed = typeof value === "number" ? value : parseFloat(String(value ?? ""));
  return Number.isFinite(parsed) ? parsed : 0;
};

// The dashboard API returns picture as a relative "/media/..." path.
// Prefix BASE_URL so the browser resolves it against the API host, not
// the frontend origin. Absolute URLs pass through unchanged.
const resolvePictureUrl = (picture: unknown): string | null => {
  if (!picture || typeof picture !== "string") return null;
  if (/^https?:\/\//i.test(picture)) return picture;
  return `${BASE_URL}${picture.startsWith("/") ? "" : "/"}${picture}`;
};

const DishPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    toasts,
    removeToast,
    success: showSuccess,
    error: showError,
  } = useToast();
  const { user } = useAuth();
  const nutritionistId = user?.nutrist_id;
  const [activeTab, setActiveTab] = useState<TabType>("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  // Creator filter holds the selected nutritionist_id (as string). Empty
  // string means "all creators" and skips the created_by API param.
  const [creatorFilter, setCreatorFilter] = useState("");
  const [openMenuId, setOpenMenuId] = useState<number | null>(null);
  // Dish ids whose picture URL failed to load — fall back to the placeholder.
  const [brokenImageIds, setBrokenImageIds] = useState<Set<number>>(new Set());

  // Nutritionists list backs the creator dropdown. Loaded once on mount.
  type NutritionistOption = { id: string; name: string };
  const [nutritionists, setNutritionists] = useState<NutritionistOption[]>([]);

  // Server-driven list state.
  const [dishes, setDishes] = useState<Dish[]>([]);
  const [recordsTotal, setRecordsTotal] = useState(0);
  const [pagesTotal, setPagesTotal] = useState(1);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  /* Page cache keyed by "<tab>|<search>|<creator>|<page>". When the user
     paginates, already-fetched pages are served from here instantly and
     the next page is silently prefetched in the background. Reset when
     the filter set changes so stale results never leak across filters. */
  type PageEntry = {
    rows: Dish[];
    recordsTotal: number;
    pagesTotal: number;
  };
  const pageCacheRef = useRef<Map<string, PageEntry>>(new Map());
  const inflightRef = useRef<Map<string, Promise<PageEntry | null>>>(new Map());
  // Guard against HMR keeping a ref that was originally initialised with a
  // different shape (e.g. an earlier version stored a Set here).
  if (!(pageCacheRef.current instanceof Map)) pageCacheRef.current = new Map();
  if (!(inflightRef.current instanceof Map)) inflightRef.current = new Map();
  const cacheKey = useCallback(
    (page: number) => `${activeTab}|${debouncedSearch}|${creatorFilter}|${page}`,
    [activeTab, debouncedSearch, creatorFilter],
  );

  // Archive / restore flow: which dish the reason dialog is targeting and
  // which mode (destructive vs restorative) it should render.
  const [reasonTarget, setReasonTarget] = useState<{
    id: number;
    name: string;
    mode: ReasonModalMode;
  } | null>(null);

  // Bulk upload flow: dropdown → picker modal → preview modal → mock upload.
  // Mirrors the ingredient list wiring so both flows read the same way.
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [uploadMode, setUploadMode] = useState<"import" | "modify">("import");
  const [bulkMenuOpen, setBulkMenuOpen] = useState(false);
  const [previewFile, setPreviewFile] = useState<File | null>(null);
  const [uploadInFlight, setUploadInFlight] = useState(false);
  const [uploadResult, setUploadResult] =
    useState<UploadDishBulkResponse | null>(null);

  /* Pick up a deferred success toast from a redirecting page (e.g. the
     View page after an archive/restore). Ref guards against React 18
     StrictMode's double-invoke; nav state is stripped so the toast
     doesn't re-fire on refresh or back-navigation. */
  const toastFiredRef = useRef(false);
  useEffect(() => {
    if (toastFiredRef.current) return;
    const state = location.state as
      | {
          showSuccessToast?: boolean;
          toastMessage?: string;
          statusFlip?: { id: number; nextStatus: Dish["status"] };
        }
      | null;
    if (state?.showSuccessToast) {
      toastFiredRef.current = true;
      showSuccess(state.toastMessage || "Success");
      // If the view-page flow told us which row changed, mirror the
      // change into local state so the list reflects the new status too.
      if (state.statusFlip) {
        const flip = state.statusFlip;
        setDishes((rows) =>
          rows.map((d) =>
            d.id === flip.id ? { ...d, status: flip.nextStatus } : d,
          ),
        );
      }
      navigate(location.pathname, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounce the search input to avoid firing a request on every keystroke.
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Invalidate the page cache whenever the filter set changes so stale
  // pages from a previous tab/search/creator never leak in.
  useEffect(() => {
    pageCacheRef.current.clear();
    inflightRef.current.clear();
    setCurrentPage(1);
  }, [activeTab, debouncedSearch, creatorFilter]);

  // Fetch the nutritionists list once on mount to back the creator
  // dropdown. Values are ids so the dashboard API can filter server-side.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const api = await getAvailableNutritionistsList();
        if (cancelled) return;
        const isSuccess =
          api?.success === true ||
          api?.success === "1" ||
          api?.success === 1 ||
          api?.success === "true";
        if (!isSuccess) return;
        const list: any[] = Array.isArray(api?.data) ? api.data : [];
        setNutritionists(
          list.map((n) => ({
            id: String(n.nutrist_id),
            name: [n.nutrist_title, n.nutrist_fname, n.nutrist_lname]
              .filter((s) => typeof s === "string" && s.trim())
              .join(" ")
              .trim() || `Nutritionist #${n.nutrist_id}`,
          })),
        );
      } catch {
        // Dropdown just stays empty on failure — not blocking.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  /* Fetch a single page and store it in the cache. `background=true` fires
     the request without touching loading/error UI state — used to warm up
     the next page while the user is still reading the current one. */
  const fetchPage = useCallback(
    (page: number, background: boolean): Promise<PageEntry | null> => {
      const key = cacheKey(page);
      const cached = pageCacheRef.current.get(key);
      if (cached) return Promise.resolve(cached);
      const existing = inflightRef.current.get(key);
      if (existing) return existing;
      const promise = (async (): Promise<PageEntry | null> => {
        try {
          const api = await getDishDashboardList(
            TAB_TO_FILTER[activeTab],
            creatorFilter,
            debouncedSearch,
            page,
            ITEMS_PER_PAGE,
          );
          const isSuccess =
            api?.success === true ||
            api?.success === "1" ||
            api?.success === 1 ||
            api?.success === "true";
          if (!isSuccess) {
            if (!background) {
              setErrorMsg(api?.message || "Failed to load dishes");
            }
            return null;
          }
          const rows: any[] = Array.isArray(api.dish_data) ? api.dish_data : [];
          const mapped: Dish[] = rows.map((r) => ({
            id: toNumber(r.dish_id),
            name: r.dish_name ?? "",
            imageUrl: resolvePictureUrl(r.picture),
            createdBy: r.created_by ? String(r.created_by) : "",
            calories: toNumber(r.cals),
            adjProtein: toNumber(r.AAFAdj_protein),
            carbs: toNumber(r.carbs),
            netCarbs: toNumber(r.net_carbs),
            fats: toNumber(r.total_fat),
            gl: toNumber(r.GL),
            status: r.hide_status ? "Archived" : "Active",
          }));
          const entry: PageEntry = {
            rows: mapped,
            recordsTotal: toNumber(
              api.recordsTotal ?? api.recordsFiltered ?? mapped.length,
            ),
            pagesTotal: Math.max(1, toNumber(api.pagesTotal ?? 1)),
          };
          pageCacheRef.current.set(key, entry);
          return entry;
        } catch (err) {
          if (!background) {
            setErrorMsg(
              err instanceof Error ? err.message : "Failed to load dishes",
            );
          }
          return null;
        } finally {
          inflightRef.current.delete(key);
        }
      })();
      inflightRef.current.set(key, promise);
      return promise;
    },
    [activeTab, debouncedSearch, creatorFilter, cacheKey],
  );

  // Load the current page (from cache if possible) and silently prefetch
  // the next page in the background so subsequent clicks are instant.
  useEffect(() => {
    let cancelled = false;
    const cached = pageCacheRef.current.get(cacheKey(currentPage));
    if (cached) {
      setDishes(cached.rows);
      setRecordsTotal(cached.recordsTotal);
      setPagesTotal(cached.pagesTotal);
      setErrorMsg(null);
      setLoading(false);
    } else {
      setLoading(true);
      setErrorMsg(null);
      fetchPage(currentPage, false).then((entry) => {
        if (cancelled) return;
        if (entry) {
          setDishes(entry.rows);
          setRecordsTotal(entry.recordsTotal);
          setPagesTotal(entry.pagesTotal);
        } else {
          setDishes([]);
          setRecordsTotal(0);
          setPagesTotal(1);
        }
        setLoading(false);
      });
    }
    return () => {
      cancelled = true;
    };
  }, [currentPage, cacheKey, fetchPage]);

  // Prefetch the next page whenever the current view or its metadata
  // changes. Runs after the visible page is available so we know the
  // total page count before deciding whether to warm the next slot.
  useEffect(() => {
    if (currentPage >= pagesTotal) return;
    const nextKey = cacheKey(currentPage + 1);
    if (pageCacheRef.current.has(nextKey)) return;
    fetchPage(currentPage + 1, true);
  }, [currentPage, pagesTotal, cacheKey, fetchPage]);

  const creators = useMemo(
    () => [
      { value: "", label: "All creators" },
      ...nutritionists.map((n) => ({ value: n.id, label: n.name })),
    ],
    [nutritionists],
  );

  const paged = dishes;
  const totalPages = pagesTotal;

  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
    setCurrentPage(1);
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  return (
    <div
      className="h-screen flex flex-col bg-[#F7F7FA]"
      onClick={() => {
        setOpenMenuId(null);
        setBulkMenuOpen(false);
      }}
    >
      <PageHeader
        title="Dish"
        rightContent={
          <div className="flex items-center gap-2 sm:gap-3">
            <div
              className="relative"
              onClick={(e) => e.stopPropagation()}
            >
              <Button
                variant="secondary"
                onClick={() => setBulkMenuOpen((prev) => !prev)}
                startIcon={<Upload className="w-4 h-4" />}
                className="whitespace-nowrap"
              >
                Bulk upload
              </Button>
              {bulkMenuOpen && (
                <div className="absolute right-0 top-full mt-2 z-20 bg-white border border-gray-200 rounded-md shadow-lg py-1 w-44">
                  <button
                    onClick={() => {
                      setBulkMenuOpen(false);
                      setUploadMode("import");
                      setUploadModalOpen(true);
                    }}
                    className="w-full text-left px-4 py-2 text-sm 3xl:text-[16px] font-inter text-[#1F1F1F] hover:bg-gray-50 transition-colors"
                  >
                    Import new
                  </button>
                  <button
                    onClick={() => {
                      setBulkMenuOpen(false);
                      setUploadMode("modify");
                      setUploadModalOpen(true);
                    }}
                    className="w-full text-left px-4 py-2 text-sm 3xl:text-[16px] font-inter text-[#1F1F1F] hover:bg-gray-50 transition-colors"
                  >
                    Modify existing
                  </button>
                </div>
              )}
            </div>
            <Button
              variant="primary"
              onClick={() => navigate("/food-library/dish/add")}
              startIcon={<Plus className="w-4 h-4" />}
              className="whitespace-nowrap"
            >
              Add dish
            </Button>
          </div>
        }
      />

      <div className="px-4 sm:px-6 lg:px-8 pb-4 flex-1 flex flex-col overflow-hidden">
        <div className="bg-white rounded-[6px] shadow-sm flex-1 flex flex-col border-[0.5px] border-[#F3F4F6] overflow-hidden">
          {/* Card Header */}
          <div className="p-4 sm:px-6 sm:py-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 flex-shrink-0">
            <h3 className="text-lg sm:text-xl 3xl:text-[22px] font-semibold font-inter text-gray-900">
              Total dishes ({recordsTotal})
            </h3>
            <div className="flex items-center gap-3 w-full sm:w-auto">
              <div className="relative flex-1 sm:flex-none">
                <input
                  type="text"
                  placeholder="Search dishes"
                  value={searchQuery}
                  onChange={handleSearch}
                  className="w-full sm:w-[260px] pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-teal-400 text-sm 3xl:text-[16px] font-inter"
                />
                <Search className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              </div>
              <div className="w-40">
                <CustomDropdown
                  options={creators}
                  value={creatorFilter}
                  onChange={setCreatorFilter}
                  placeholder="All creators"
                />
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="px-4 sm:px-6 flex gap-6 border-b border-gray-200 flex-shrink-0">
            {(
              [
                { key: "all", label: "All" },
                { key: "active", label: "Active" },
                { key: "archived", label: "Archived" },
              ] as { key: TabType; label: string }[]
            ).map((tab) => (
              <button
                key={tab.key}
                onClick={() => handleTabChange(tab.key)}
                className={`pb-3 px-1 relative text-sm sm:text-base 3xl:text-[16px] font-medium font-inter transition-colors ${
                  activeTab === tab.key
                    ? "text-[#0DB5B5]"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                {tab.label}
                {activeTab === tab.key && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#0DB5B5]"></div>
                )}
              </button>
            ))}
          </div>

          {/* Table */}
          <div className="flex-1 overflow-hidden flex flex-col">
            {/* Fixed Header */}
            <div className="flex-shrink-0 overflow-x-auto bg-white border-b border-[#111C2D33] mx-5">
              <table className="w-full min-w-[960px] table-fixed">
                <thead>
                  <tr className="bg-white">
                    <th className="text-left py-3 sm:py-4 px-4 sm:px-6 text-[#646464] text-xs sm:text-sm 3xl:text-[16px] font-inter font-normal w-[22%]">
                      Dish name
                    </th>
                    <th className="text-left py-3 sm:py-4 px-4 sm:px-6 text-[#646464] text-xs sm:text-sm 3xl:text-[16px] font-inter font-normal w-[16%]">
                      Created by
                    </th>
                    <th className="text-left py-3 sm:py-4 px-4 sm:px-6 text-[#646464] text-xs sm:text-sm 3xl:text-[16px] font-inter font-normal w-[8%]">
                      Calories
                    </th>
                    <th className="text-left py-3 sm:py-4 px-4 sm:px-6 text-[#646464] text-xs sm:text-sm 3xl:text-[16px] font-inter font-normal w-[9%]">
                      Adj. protein
                    </th>
                    <th className="text-left py-3 sm:py-4 px-4 sm:px-6 text-[#646464] text-xs sm:text-sm 3xl:text-[16px] font-inter font-normal w-[8%]">
                      Carbs
                    </th>
                    <th className="text-left py-3 sm:py-4 px-4 sm:px-6 text-[#646464] text-xs sm:text-sm 3xl:text-[16px] font-inter font-normal w-[9%]">
                      Net carbs
                    </th>
                    <th className="text-left py-3 sm:py-4 px-4 sm:px-6 text-[#646464] text-xs sm:text-sm 3xl:text-[16px] font-inter font-normal w-[7%]">
                      Fats
                    </th>
                    <th className="text-left py-3 sm:py-4 px-4 sm:px-6 text-[#646464] text-xs sm:text-sm 3xl:text-[16px] font-inter font-normal w-[7%]">
                      GL
                    </th>
                    <th className="text-left py-3 sm:py-4 px-4 sm:px-6 text-[#646464] text-xs sm:text-sm 3xl:text-[16px] font-inter font-normal w-[10%]">
                      Status
                    </th>
                    <th className="w-[4%]" />
                  </tr>
                </thead>
              </table>
            </div>

            {/* Scrollable Body */}
            <div className="flex-1 overflow-x-auto overflow-y-auto mx-5 custom-scrollbar-overlay">
              {loading ? (
                <div className="flex items-center justify-center h-full py-16">
                  <p className="text-gray-400 text-sm sm:text-base 3xl:text-[18px] font-inter">
                    Loading dishes...
                  </p>
                </div>
              ) : errorMsg ? (
                <div className="flex items-center justify-center h-full py-16">
                  <p className="text-red-500 text-sm sm:text-base 3xl:text-[18px] font-inter">
                    {errorMsg}
                  </p>
                </div>
              ) : paged.length === 0 ? (
                <div className="flex items-center justify-center h-full py-16">
                  <p className="text-gray-400 text-sm sm:text-base 3xl:text-[18px] font-inter">
                    {searchQuery.trim()
                      ? "No dishes match your search"
                      : "No dishes yet"}
                  </p>
                </div>
              ) : (
                <table className="w-full min-w-[960px] table-fixed">
                  <tbody>
                    {paged.map((dish, index) => {
                      const isLast = index === paged.length - 1;
                      const borderCls = isLast ? "" : "border-b border-[#111C2D1A]";
                      return (
                        <tr
                          key={dish.id}
                          onClick={() => navigate(`/food-library/dish/${dish.id}`)}
                          className="hover:bg-gray-50 transition-colors cursor-pointer"
                        >
                          {/* Dish name */}
                          <td className={`py-3 sm:py-4 px-4 sm:px-6 w-[22%] ${borderCls}`}>
                            <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                              {dish.imageUrl && !brokenImageIds.has(dish.id) ? (
                                <img
                                  src={dish.imageUrl}
                                  alt={dish.name}
                                  onError={() =>
                                    setBrokenImageIds((prev) => {
                                      if (prev.has(dish.id)) return prev;
                                      const next = new Set(prev);
                                      next.add(dish.id);
                                      return next;
                                    })
                                  }
                                  className="w-8 h-8 sm:w-9 sm:h-9 rounded-[4px] object-cover flex-shrink-0"
                                />
                              ) : (
                                <NoImageIcon className="w-8 h-8 sm:w-9 sm:h-9 rounded-[4px] flex-shrink-0" />
                              )}
                              <span
                                title={dish.name}
                                className="font-medium font-inter text-[#1F1F1F] text-sm sm:text-base 3xl:text-[18px] truncate min-w-0 flex-1"
                              >
                                {dish.name}
                              </span>
                            </div>
                          </td>

                          {/* Created by */}
                          <td className={`py-3 sm:py-4 px-4 sm:px-6 text-[#1F1F1F] text-sm sm:text-base 3xl:text-[18px] font-inter w-[16%] ${borderCls}`}>
                            {dish.createdBy}
                          </td>

                          {/* Calories */}
                          <td className={`py-3 sm:py-4 px-4 sm:px-6 text-[#1F1F1F] text-sm sm:text-base 3xl:text-[18px] font-inter w-[8%] ${borderCls}`}>
                            {dish.calories}
                          </td>

                          {/* Adj. protein */}
                          <td className={`py-3 sm:py-4 px-4 sm:px-6 text-[#1F1F1F] text-sm sm:text-base 3xl:text-[18px] font-inter w-[9%] ${borderCls}`}>
                            {dish.adjProtein}
                          </td>

                          {/* Carbs */}
                          <td className={`py-3 sm:py-4 px-4 sm:px-6 text-[#1F1F1F] text-sm sm:text-base 3xl:text-[18px] font-inter w-[8%] ${borderCls}`}>
                            {dish.carbs}
                          </td>

                          {/* Net carbs */}
                          <td className={`py-3 sm:py-4 px-4 sm:px-6 text-[#1F1F1F] text-sm sm:text-base 3xl:text-[18px] font-inter w-[9%] ${borderCls}`}>
                            {dish.netCarbs}
                          </td>

                          {/* Fats */}
                          <td className={`py-3 sm:py-4 px-4 sm:px-6 text-[#1F1F1F] text-sm sm:text-base 3xl:text-[18px] font-inter w-[7%] ${borderCls}`}>
                            {dish.fats}
                          </td>

                          {/* GL */}
                          <td className={`py-3 sm:py-4 px-4 sm:px-6 text-[#1F1F1F] text-sm sm:text-base 3xl:text-[18px] font-inter w-[7%] ${borderCls}`}>
                            {dish.gl}
                          </td>

                          {/* Status */}
                          <td className={`py-3 sm:py-4 px-4 sm:px-6 w-[10%] ${borderCls}`}>
                            <span
                              className={`inline-flex items-center px-3 py-1 rounded-full text-xs 3xl:text-[14px] font-semibold font-inter ${
                                dish.status === "Active"
                                  ? "bg-[#EBF7F4] text-[#0DB5B5]"
                                  : "bg-gray-100 text-gray-500"
                              }`}
                            >
                              {dish.status}
                            </span>
                          </td>

                          {/* Three-dot menu */}
                          <td className={`py-3 sm:py-4 pr-4 w-[4%] ${borderCls}`}>
                            <div
                              className="relative"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <button
                                onClick={() =>
                                  setOpenMenuId((prev) =>
                                    prev === dish.id ? null : dish.id,
                                  )
                                }
                                className="p-1 rounded hover:bg-gray-100 transition-colors"
                              >
                                <MoreVertical className="w-4 h-4 text-gray-500" />
                              </button>
                              {openMenuId === dish.id && (
                                <div className="absolute right-0 top-8 z-10 bg-white border border-gray-200 rounded-lg shadow-lg py-1 w-40">
                                  <button
                                    onClick={() => {
                                      setOpenMenuId(null);
                                      navigate(`/food-library/dish/${dish.id}`);
                                    }}
                                    className="w-full text-left px-4 py-2 text-sm 3xl:text-[16px] font-inter text-[#1F1F1F] hover:bg-gray-50 transition-colors"
                                  >
                                    View
                                  </button>
                                  {dish.status === "Archived" ? (
                                    <button
                                      onClick={() => {
                                        setOpenMenuId(null);
                                        setReasonTarget({
                                          id: dish.id,
                                          name: dish.name,
                                          mode: "restore",
                                        });
                                      }}
                                      className="w-full text-left px-4 py-2 text-sm 3xl:text-[16px] font-inter text-[#1F1F1F] hover:bg-gray-50 transition-colors"
                                    >
                                      Restore
                                    </button>
                                  ) : (
                                    <>
                                      <button
                                        onClick={() => {
                                          setOpenMenuId(null);
                                          navigate(`/food-library/dish/${dish.id}/edit`);
                                        }}
                                        className="w-full text-left px-4 py-2 text-sm 3xl:text-[16px] font-inter text-[#1F1F1F] hover:bg-gray-50 transition-colors"
                                      >
                                        Edit
                                      </button>
                                      <button
                                        onClick={() => {
                                          setOpenMenuId(null);
                                          setReasonTarget({
                                            id: dish.id,
                                            name: dish.name,
                                            mode: "archive",
                                          });
                                        }}
                                        className="w-full text-left px-4 py-2 text-sm 3xl:text-[16px] font-inter text-[#1F1F1F] hover:bg-gray-50 transition-colors"
                                      >
                                        Archive
                                      </button>
                                    </>
                                  )}
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {/* Pagination */}
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            resultCount={paged.length}
          />
        </div>
      </div>

      {/* Bulk upload — dropdown → picker modal → preview modal → mock upload.
          The picker just picks a file for both flows; the sheet preview and
          upload API step is shared and runs from the preview modal below. */}
      <BulkUploadModal
        open={uploadModalOpen}
        mode={uploadMode}
        label="dishes"
        onClose={() => setUploadModalOpen(false)}
        onImport={(file) => {
          setUploadModalOpen(false);
          setPreviewFile(file);
        }}
        onDownloadTemplate={async () => {
          // Modal mode maps to the backend `variant` param: Import new sends
          // the blank template ("normal"), Modify existing sends the
          // pre-filled one ("rename") so the user can edit and re-upload.
          try {
            await downloadDishBulkUploadTemplate(
              uploadMode === "modify" ? "rename" : "normal",
            );
          } catch (err) {
            showError(
              "Failed to download template",
              err instanceof Error ? err.message : undefined,
            );
          }
        }}
      />

      <BulkUploadPreviewModal
        open={previewFile !== null}
        file={previewFile}
        mode={uploadMode}
        uploadResult={uploadResult}
        uploadInFlight={uploadInFlight}
        onDownloadErrors={
          previewFile && nutritionistId
            ? () => downloadDishBulkUploadErrors(nutritionistId, previewFile)
            : undefined
        }
        onClose={() => {
          setPreviewFile(null);
          setUploadResult(null);
          setUploadInFlight(false);
        }}
        onImport={async (file) => {
          // Shared for both Import new and Modify existing — the preview
          // has already let the user eyeball the sheet; this Import /
          // Validate click posts it and keeps the modal open so the
          // response renders in place.
          if (!nutritionistId) {
            showError("You are not signed in", "Please sign in and retry.");
            return;
          }
          setUploadInFlight(true);
          try {
            const res = await uploadDishBulkUpload(nutritionistId, file);
            setUploadResult(res);
            const isSuccess = res.success === "1" || res.success === "true";
            if (isSuccess) {
              // Row set changed → invalidate the cache and refetch so the
              // current view reflects added / updated dishes.
              pageCacheRef.current.clear();
              inflightRef.current.clear();
              const entry = await fetchPage(currentPage, false);
              if (entry) {
                setDishes(entry.rows);
                setRecordsTotal(entry.recordsTotal);
                setPagesTotal(entry.pagesTotal);
              }
            }
          } catch (err) {
            setUploadResult({
              success: "0",
              message:
                err instanceof Error
                  ? err.message
                  : "Failed to upload file. Please try again.",
            });
          } finally {
            setUploadInFlight(false);
          }
        }}
      />

      <IngredientReasonModal
        open={reasonTarget !== null}
        mode={reasonTarget?.mode ?? "archive"}
        itemLabel="dish"
        ingredientName={reasonTarget?.name}
        onClose={() => setReasonTarget(null)}
        onConfirm={async (reason) => {
          const target = reasonTarget;
          if (!target) return;
          const isRestore = target.mode === "restore";
          if (!nutritionistId) {
            showError("You are not signed in", "Please sign in and retry.");
            return;
          }
          try {
            const api = await archiveOrRestoreDish({
              dish_id: target.id,
              is_hide: !isRestore,
              reason,
              nutritionist_id: nutritionistId,
            });
            const isSuccess =
              api?.success === true ||
              api?.success === "1" ||
              api?.success === 1 ||
              api?.success === "true";
            if (!isSuccess) {
              throw new Error(
                api?.message ||
                  (isRestore
                    ? "Failed to restore dish"
                    : "Failed to archive dish"),
              );
            }
            // Item's status changed → cached pages are stale across tabs.
            pageCacheRef.current.clear();
            inflightRef.current.clear();
            setReasonTarget(null);
            const dishName = api?.dish_name || target.name;
            showSuccess(
              api?.message ||
                (isRestore
                  ? "Dish restored successfully"
                  : "Dish archived successfully"),
              isRestore
                ? `${dishName} has been moved to Active.`
                : `${dishName} has been moved to Archived.`,
            );
            // Refetch the current page so the row reflects the new status.
            setLoading(true);
            const entry = await fetchPage(currentPage, false);
            if (entry) {
              setDishes(entry.rows);
              setRecordsTotal(entry.recordsTotal);
              setPagesTotal(entry.pagesTotal);
            }
            setLoading(false);
          } catch (err) {
            setReasonTarget(null);
            showError(
              isRestore ? "Failed to restore dish" : "Failed to archive dish",
              err instanceof Error ? err.message : undefined,
            );
          }
        }}
      />

      <ToastContainer toasts={toasts} onClose={removeToast} />
    </div>
  );
};

export default DishPage;



// import React from "react";

// const DishPage: React.FC = () => {
//   return <div>dish page</div>;
// };

// export default DishPage;